/**
 * Utilitaires pour le stockage persistant
 * Gestion de localStorage, IndexedDB et Cache API
 */

import { openDB } from 'idb';
import type { DBSchema, IDBPDatabase } from 'idb';
import type { Favorite, HistoryEntry, Settings, CacheStats } from '@/types';

// Schéma de la base de données IndexedDB
interface JEMAppDB extends DBSchema {
  favorites: {
    key: number;
    value: Favorite;
    indexes: { 'by-date': number };
  };
  history: {
    key: number;
    value: HistoryEntry;
    indexes: { 'by-timestamp': number };
  };
  cache: {
    key: string;
    value: {
      type: 'pdf' | 'midi';
      refCode: string;
      data: ArrayBuffer;
      timestamp: number;
      size: number;
    };
  };
}

const DB_NAME = 'jem-app-db';
const DB_VERSION = 1;

// Instance de la base de données
let dbInstance: IDBPDatabase<JEMAppDB> | null = null;

// Initialiser la base de données
export async function initDB(): Promise<IDBPDatabase<JEMAppDB>> {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<JEMAppDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Store pour les favoris
      if (!db.objectStoreNames.contains('favorites')) {
        const favStore = db.createObjectStore('favorites', { keyPath: 'songId' });
        favStore.createIndex('by-date', 'addedAt');
      }

      // Store pour l'historique
      if (!db.objectStoreNames.contains('history')) {
        const histStore = db.createObjectStore('history', { keyPath: 'songId' });
        histStore.createIndex('by-timestamp', 'timestamp');
      }

      // Store pour le cache manuel
      if (!db.objectStoreNames.contains('cache')) {
        db.createObjectStore('cache', { keyPath: 'refCode' });
      }
    },
  });

  return dbInstance;
}

// ============= SETTINGS (localStorage) =============

const SETTINGS_KEY = 'jem-settings';

export function getSettings(): Settings {
  try {
    const stored = localStorage.getItem(SETTINGS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading settings:', error);
  }

  return {
    theme: 'light',
    textSize: 'normal',
    avatar: 'default',
  };
}

export function saveSettings(settings: Settings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Error saving settings:', error);
  }
}

// ============= FAVORITES =============

export async function getFavorites(): Promise<Favorite[]> {
  const db = await initDB();
  return db.getAll('favorites');
}

export async function addFavorite(songId: number): Promise<void> {
  const db = await initDB();
  await db.put('favorites', {
    songId,
    addedAt: Date.now(),
  });
}

export async function removeFavorite(songId: number): Promise<void> {
  const db = await initDB();
  await db.delete('favorites', songId);
}

export async function isFavorite(songId: number): Promise<boolean> {
  const db = await initDB();
  const fav = await db.get('favorites', songId);
  return !!fav;
}

// ============= HISTORY =============

export async function getHistory(): Promise<HistoryEntry[]> {
  const db = await initDB();
  const entries = await db.getAll('history');
  // Trier par timestamp décroissant
  return entries.sort((a, b) => b.timestamp - a.timestamp);
}

export async function addHistoryEntry(
  songId: number,
  duration: number,
  deviceWidth: number
): Promise<void> {
  const db = await initDB();
  const existing = await db.get('history', songId);

  if (existing) {
    // Mettre à jour l'entrée existante
    await db.put('history', {
      songId,
      timestamp: Date.now(),
      duration: existing.duration + duration,
      deviceWidth,
    });
  } else {
    // Créer une nouvelle entrée
    await db.put('history', {
      songId,
      timestamp: Date.now(),
      duration,
      deviceWidth,
    });
  }
}

export async function removeHistoryEntry(songId: number): Promise<void> {
  const db = await initDB();
  await db.delete('history', songId);
}

export async function clearHistory(): Promise<void> {
  const db = await initDB();
  await db.clear('history');
}

// ============= CACHE MANUEL (PDF/MIDI) =============

export async function cacheFile(
  type: 'pdf' | 'midi',
  refCode: string,
  data: ArrayBuffer
): Promise<void> {
  try {
    const db = await initDB();
    await db.put('cache', {
      type,
      refCode,
      data,
      timestamp: Date.now(),
      size: data.byteLength,
    });
  } catch (error: any) {
    if (error.name === 'QuotaExceededError') {
      throw new Error('QUOTA_EXCEEDED');
    }
    throw error;
  }
}

export async function getCachedFile(refCode: string): Promise<ArrayBuffer | null> {
  const db = await initDB();
  const cached = await db.get('cache', refCode);
  return cached?.data || null;
}

export async function isCached(refCode: string): Promise<boolean> {
  const db = await initDB();
  const cached = await db.get('cache', refCode);
  return !!cached;
}

export async function removeCachedFile(refCode: string): Promise<void> {
  const db = await initDB();
  await db.delete('cache', refCode);
}

export async function getCacheStats(): Promise<CacheStats> {
  const db = await initDB();
  const allCached = await db.getAll('cache');

  let pdfCount = 0;
  let midiCount = 0;
  let totalSize = 0;

  allCached.forEach(item => {
    if (item.type === 'pdf') pdfCount++;
    else midiCount++;
    totalSize += item.size;
  });

  // Obtenir les informations de quota
  let quota = 0;
  let usage = 0;

  if ('storage' in navigator && 'estimate' in navigator.storage) {
    const estimate = await navigator.storage.estimate();
    quota = estimate.quota || 0;
    usage = estimate.usage || 0;
  }

  return {
    pdfCount,
    midiCount,
    totalSize,
    quota,
    usage,
  };
}

export async function clearCache(): Promise<void> {
  const db = await initDB();
  await db.clear('cache');
}

// ============= EXPORT / IMPORT =============

export async function exportFavorites(): Promise<string> {
  const favorites = await getFavorites();
  return JSON.stringify(favorites, null, 2);
}

export async function importFavorites(jsonData: string): Promise<number> {
  try {
    const favorites: Favorite[] = JSON.parse(jsonData);
    const db = await initDB();

    let count = 0;
    for (const fav of favorites) {
      await db.put('favorites', fav);
      count++;
    }

    return count;
  } catch (error) {
    console.error('Error importing favorites:', error);
    throw new Error('Format de données invalide');
  }
}

export async function exportHistory(): Promise<string> {
  const history = await getHistory();
  return JSON.stringify(history, null, 2);
}

export async function importHistory(jsonData: string): Promise<number> {
  try {
    const history: HistoryEntry[] = JSON.parse(jsonData);
    const db = await initDB();

    let count = 0;
    for (const entry of history) {
      await db.put('history', entry);
      count++;
    }

    return count;
  } catch (error) {
    console.error('Error importing history:', error);
    throw new Error('Format de données invalide');
  }
}
